import { useState, useMemo } from 'react';
import { Box, Button, TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem, Typography, Collapse, Chip, Grid, Checkbox, ListItemText } from '@mui/material';
import { Search, ChevronDown, ChevronUp, X, Filter, ArrowUpDown } from 'lucide-react';
import { tokens } from '@/theme/tokens';

import { STUDENT_STATUSES, ENROLLMENT_STATUSES, GENDERS_FILTER as GENDERS, BLOOD_GROUPS, RELIGIONS_FILTER as RELIGIONS, CATEGORIES_FILTER as CATEGORIES, CASTES_FILTER as CASTES, NATIONALITIES, ADMISSION_TYPES_FILTER as ADMISSION_TYPES, SORT_BY_OPTIONS, SORT_ORDER_OPTIONS } from '@/constants/student';

export function StudentListFilters({
  advancedFilters,
  setAdvancedFilters,
  sessions,
  classesData,
  onApply,
  onClear,
}) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleFilterChange = (key, value) => {
    setAdvancedFilters((prev) => {
      const updated = { ...prev, [key]: value };
      if (key === 'student_statuses') {
        updated.student_status = Array.isArray(value) && value.length === 1 ? value[0] : '';
      }
      if (key === 'enrollment_statuses') {
        updated.enrollment_status = Array.isArray(value) && value.length === 1 ? value[0] : '';
      }
      return updated;
    });
  };

  // Sections for all currently selected classes
  const selectedClassIds = Array.isArray(advancedFilters.class_ids) ? advancedFilters.class_ids : (advancedFilters.class_id ? [advancedFilters.class_id] : []);
  
  const sectionsOptions = useMemo(() => {
    if (selectedClassIds.length === 0) {
      return classesData.flatMap(c => (c.sections || []).map(sec => ({ ...sec, class_name: c.class_name })));
    }
    return classesData
      .filter(c => selectedClassIds.includes(c.id))
      .flatMap(c => (c.sections || []).map(sec => ({ ...sec, class_name: c.class_name })));
  }, [classesData, selectedClassIds]);

  // Generate active filter chips
  const activeChips = useMemo(() => {
    const chips = [];
    if (advancedFilters.search) chips.push({ key: 'search', label: `Search: ${advancedFilters.search}` });

    // Sessions (multi)
    const sessionIds = Array.isArray(advancedFilters.academic_session_ids) 
      ? advancedFilters.academic_session_ids 
      : (advancedFilters.academic_session_id ? [advancedFilters.academic_session_id] : []);
    if (sessionIds.length > 0) {
      const names = sessionIds.map(id => {
        const s = sessions.find(x => x.id === id);
        return s?.session_name || 'Session';
      });
      chips.push({ key: 'academic_session_ids', label: `Sessions: ${names.join(', ')}` });
    }

    // Classes (multi)
    if (selectedClassIds.length > 0) {
      const names = selectedClassIds.map(id => {
        const c = classesData.find(x => x.id === id);
        return c?.class_name || 'Class';
      });
      chips.push({ key: 'class_ids', label: `Classes: ${names.join(', ')}` });
    }

    // Sections (multi)
    const sectionIds = Array.isArray(advancedFilters.section_ids) 
      ? advancedFilters.section_ids 
      : (advancedFilters.section_id ? [advancedFilters.section_id] : []);
    if (sectionIds.length > 0) {
      const names = sectionIds.map(id => {
        const sec = sectionsOptions.find(x => x.id === id);
        return sec?.section_name || 'Section';
      });
      chips.push({ key: 'section_ids', label: `Sections: ${names.join(', ')}` });
    }

    // Multi-select array filters
    const multiKeys = [
      { key: 'student_statuses', label: 'Statuses' },
      { key: 'enrollment_statuses', label: 'Enrollments' },
      { key: 'genders', label: 'Genders' },
      { key: 'blood_groups', label: 'Blood Groups' },
      { key: 'religions', label: 'Religions' },
      { key: 'categories', label: 'Categories' },
      { key: 'castes', label: 'Castes' },
      { key: 'nationalities', label: 'Nationalities' },
      { key: 'admission_types', label: 'Admission Types' },
    ];

    multiKeys.forEach(({ key, label }) => {
      const val = advancedFilters[key];
      if (Array.isArray(val) && val.length > 0) {
        chips.push({ key, label: `${label}: ${val.join(', ')}` });
      }
    });

    // Single flags and booleans
    const flagKeys = [
      { key: 'is_rte', label: 'RTE' },
      { key: 'has_mobile', label: 'Has Mobile' },
      { key: 'has_email', label: 'Has Email' },
      { key: 'has_aadhaar', label: 'Has Aadhaar' },
    ];

    flagKeys.forEach(({ key, label }) => {
      const val = advancedFilters[key];
      if (val !== undefined && val !== null && val !== '') {
        chips.push({ key, label: `${label}: ${val === 'true' ? 'Yes' : 'No'}` });
      }
    });

    // Text & numeric fields
    const textKeys = [
      { key: 'admission_no', label: 'Adm No' },
      { key: 'roll_number', label: 'Roll No' },
      { key: 'admission_date_from', label: 'Adm From' },
      { key: 'admission_date_to', label: 'Adm To' },
      { key: 'date_of_birth_from', label: 'DOB From' },
      { key: 'date_of_birth_to', label: 'DOB To' },
      { key: 'father_name', label: 'Father' },
      { key: 'father_mobile', label: 'Father Mobile' },
      { key: 'father_email', label: 'Father Email' },
      { key: 'father_occupation', label: 'Father Occ' },
      { key: 'mother_name', label: 'Mother' },
      { key: 'mother_mobile', label: 'Mother Mobile' },
      { key: 'mother_email', label: 'Mother Email' },
      { key: 'mother_occupation', label: 'Mother Occ' },
      { key: 'guardian_name', label: 'Guardian' },
      { key: 'guardian_relation', label: 'Guardian Rel' },
      { key: 'guardian_mobile', label: 'Guardian Mobile' },
      { key: 'annual_income_min', label: 'Min Income' },
      { key: 'annual_income_max', label: 'Max Income' },
      { key: 'sort_by', label: 'Sort By' },
      { key: 'sort_order', label: 'Sort Order' },
    ];

    textKeys.forEach(({ key, label }) => {
      if (advancedFilters[key] !== undefined && advancedFilters[key] !== null && advancedFilters[key] !== '') {
        chips.push({ key, label: `${label}: ${advancedFilters[key]}` });
      }
    });

    return chips;
  }, [advancedFilters, sessions, classesData, selectedClassIds, sectionsOptions]);

  const removeChip = (key) => {
    setAdvancedFilters((prev) => {
      const updated = { ...prev };
      if (Array.isArray(updated[key])) {
        updated[key] = [];
      } else {
        updated[key] = '';
      }
      if (key === 'student_statuses') updated.student_status = '';
      if (key === 'enrollment_statuses') updated.enrollment_status = '';
      if (key === 'academic_session_ids') updated.academic_session_id = '';
      if (key === 'class_ids') updated.class_id = '';
      if (key === 'section_ids') updated.section_id = '';
      return updated;
    });
  };

  return (
    <Box sx={{ p: 2, borderBottom: `1px solid ${tokens.colors.border}`, display: 'flex', flexDirection: 'column', gap: 2 }}>
      
      {/* Top Bar: Always visible */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', flex: 1 }}>
          <TextField
            placeholder="Search by name, admission no, or phone..."
            variant="outlined"
            size="small"
            value={advancedFilters.search || ''}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            sx={{ flex: 1, minWidth: { xs: '100%', sm: 300 } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} color={tokens.colors.text.secondary} />
                  </InputAdornment>
                ),
              }
            }}
          />
          
          {/* Sessions Multi-Select */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Sessions</InputLabel>
            <Select
              multiple
              value={advancedFilters.academic_session_ids || []}
              label="Sessions"
              onChange={(e) => handleFilterChange('academic_session_ids', typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              renderValue={(selected) => {
                if (selected.length === 0) return <em>All Sessions</em>;
                if (selected.length === 1) {
                  const s = sessions.find(x => x.id === selected[0]);
                  return s?.session_name || '1 Session';
                }
                return `${selected.length} Sessions`;
              }}
            >
              {sessions.map((s) => {
                const isSelected = (advancedFilters.academic_session_ids || []).includes(s.id);
                return (
                  <MenuItem key={s.id} value={s.id}>
                    <Checkbox checked={isSelected} size="small" />
                    <ListItemText primary={s.session_name || `${s.start_date} - ${s.end_date}`} />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          
          {/* Classes Multi-Select */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Classes</InputLabel>
            <Select
              multiple
              value={advancedFilters.class_ids || []}
              label="Classes"
              onChange={(e) => handleFilterChange('class_ids', typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              renderValue={(selected) => {
                if (selected.length === 0) return <em>All Classes</em>;
                if (selected.length === 1) {
                  const c = classesData.find(x => x.id === selected[0]);
                  return c?.class_name || '1 Class';
                }
                return `${selected.length} Classes`;
              }}
            >
              {classesData.map((cls) => {
                const isSelected = (advancedFilters.class_ids || []).includes(cls.id);
                return (
                  <MenuItem key={cls.id} value={cls.id}>
                    <Checkbox checked={isSelected} size="small" />
                    <ListItemText primary={cls.class_name} />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          
          {/* Sections Multi-Select */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Sections</InputLabel>
            <Select
              multiple
              value={advancedFilters.section_ids || []}
              label="Sections"
              onChange={(e) => handleFilterChange('section_ids', typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              renderValue={(selected) => {
                if (selected.length === 0) return <em>All Sections</em>;
                if (selected.length === 1) {
                  const sec = sectionsOptions.find(x => x.id === selected[0]);
                  return sec ? `${sec.section_name} (${sec.class_name || 'Class'})` : '1 Section';
                }
                return `${selected.length} Sections`;
              }}
            >
              {sectionsOptions.map((section) => {
                const isSelected = (advancedFilters.section_ids || []).includes(section.id);
                return (
                  <MenuItem key={section.id} value={section.id}>
                    <Checkbox checked={isSelected} size="small" />
                    <ListItemText primary={`${section.section_name} (${section.class_name || 'Class'})`} />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Box>

        <Button 
          variant="text" 
          color="inherit" 
          onClick={() => setShowAdvanced(!showAdvanced)}
          endIcon={showAdvanced ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          sx={{ textTransform: 'none', fontWeight: 600, color: tokens.colors.text.secondary }}
        >
          {showAdvanced ? 'Hide Filters' : 'Advanced Filters'}
        </Button>
      </Box>

      {/* Advanced Filters Panel */}
      <Collapse in={showAdvanced}>
        <Box sx={{ mt: 2, pt: 2, borderTop: `1px dashed ${tokens.colors.border}` }}>
          <Typography variant="subtitle2" sx={{ mb: 2, color: tokens.colors.primary.main, fontWeight: 600 }}>
            Advanced Filters & Sorting
          </Typography>
          
          <Grid container spacing={2}>
            {/* Sorting Controls */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={advancedFilters.sort_by || ''}
                  label="Sort By"
                  onChange={(e) => handleFilterChange('sort_by', e.target.value)}
                  startAdornment={<ArrowUpDown size={16} style={{ marginRight: 6, opacity: 0.7 }} />}
                >
                  <MenuItem value=""><em>Default Sorting</em></MenuItem>
                  {SORT_BY_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Sort Order</InputLabel>
                <Select
                  value={advancedFilters.sort_order || 'asc'}
                  label="Sort Order"
                  onChange={(e) => handleFilterChange('sort_order', e.target.value)}
                >
                  {SORT_ORDER_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Statuses (Multi-select) */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Student Statuses</InputLabel>
                <Select
                  multiple
                  value={advancedFilters.student_statuses || []}
                  label="Student Statuses"
                  onChange={(e) => handleFilterChange('student_statuses', typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                  renderValue={(selected) => selected.length === 0 ? <em>All Statuses</em> : selected.join(', ')}
                >
                  {STUDENT_STATUSES.map(s => {
                    const isSelected = (advancedFilters.student_statuses || []).includes(s.value);
                    return (
                      <MenuItem key={s.value} value={s.value}>
                        <Checkbox checked={isSelected} size="small" />
                        <ListItemText primary={s.label} />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Enrollment Statuses</InputLabel>
                <Select
                  multiple
                  value={advancedFilters.enrollment_statuses || []}
                  label="Enrollment Statuses"
                  onChange={(e) => handleFilterChange('enrollment_statuses', typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                  renderValue={(selected) => selected.length === 0 ? <em>All Statuses</em> : selected.join(', ')}
                >
                  {ENROLLMENT_STATUSES.map(s => {
                    const isSelected = (advancedFilters.enrollment_statuses || []).includes(s.value);
                    return (
                      <MenuItem key={s.value} value={s.value}>
                        <Checkbox checked={isSelected} size="small" />
                        <ListItemText primary={s.label} />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>

            {/* Identifiers */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField size="small" fullWidth label="Admission No." placeholder="e.g. DEMO-00082" value={advancedFilters.admission_no || ''} onChange={(e) => handleFilterChange('admission_no', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField size="small" fullWidth label="Roll Number" placeholder="e.g. 25" value={advancedFilters.roll_number || ''} onChange={(e) => handleFilterChange('roll_number', e.target.value)} />
            </Grid>

            {/* Demographics (Multi-select) */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Genders</InputLabel>
                <Select
                  multiple
                  value={advancedFilters.genders || []}
                  label="Genders"
                  onChange={(e) => handleFilterChange('genders', typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                  renderValue={(selected) => selected.length === 0 ? <em>All Genders</em> : selected.join(', ')}
                >
                  {GENDERS.map(g => {
                    const isSelected = (advancedFilters.genders || []).includes(g.value);
                    return (
                      <MenuItem key={g.value} value={g.value}>
                        <Checkbox checked={isSelected} size="small" />
                        <ListItemText primary={g.label} />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Blood Groups</InputLabel>
                <Select
                  multiple
                  value={advancedFilters.blood_groups || []}
                  label="Blood Groups"
                  onChange={(e) => handleFilterChange('blood_groups', typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                  renderValue={(selected) => selected.length === 0 ? <em>All Groups</em> : selected.join(', ')}
                >
                  {BLOOD_GROUPS.map(bg => {
                    const isSelected = (advancedFilters.blood_groups || []).includes(bg);
                    return (
                      <MenuItem key={bg} value={bg}>
                        <Checkbox checked={isSelected} size="small" />
                        <ListItemText primary={bg} />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Categories</InputLabel>
                <Select
                  multiple
                  value={advancedFilters.categories || []}
                  label="Categories"
                  onChange={(e) => handleFilterChange('categories', typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                  renderValue={(selected) => selected.length === 0 ? <em>All Categories</em> : selected.join(', ')}
                >
                  {CATEGORIES.map(cat => {
                    const isSelected = (advancedFilters.categories || []).includes(cat);
                    return (
                      <MenuItem key={cat} value={cat}>
                        <Checkbox checked={isSelected} size="small" />
                        <ListItemText primary={cat} />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Castes</InputLabel>
                <Select
                  multiple
                  value={advancedFilters.castes || []}
                  label="Castes"
                  onChange={(e) => handleFilterChange('castes', typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                  renderValue={(selected) => selected.length === 0 ? <em>All Castes</em> : selected.join(', ')}
                >
                  {CASTES.map(c => {
                    const isSelected = (advancedFilters.castes || []).includes(c);
                    return (
                      <MenuItem key={c} value={c}>
                        <Checkbox checked={isSelected} size="small" />
                        <ListItemText primary={c} />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Religions</InputLabel>
                <Select
                  multiple
                  value={advancedFilters.religions || []}
                  label="Religions"
                  onChange={(e) => handleFilterChange('religions', typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                  renderValue={(selected) => selected.length === 0 ? <em>All Religions</em> : selected.join(', ')}
                >
                  {RELIGIONS.map(r => {
                    const isSelected = (advancedFilters.religions || []).includes(r);
                    return (
                      <MenuItem key={r} value={r}>
                        <Checkbox checked={isSelected} size="small" />
                        <ListItemText primary={r} />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Nationalities</InputLabel>
                <Select
                  multiple
                  value={advancedFilters.nationalities || []}
                  label="Nationalities"
                  onChange={(e) => handleFilterChange('nationalities', typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                  renderValue={(selected) => selected.length === 0 ? <em>All Nationalities</em> : selected.join(', ')}
                >
                  {NATIONALITIES.map(n => {
                    const isSelected = (advancedFilters.nationalities || []).includes(n);
                    return (
                      <MenuItem key={n} value={n}>
                        <Checkbox checked={isSelected} size="small" />
                        <ListItemText primary={n} />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Admission Types</InputLabel>
                <Select
                  multiple
                  value={advancedFilters.admission_types || []}
                  label="Admission Types"
                  onChange={(e) => handleFilterChange('admission_types', typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                  renderValue={(selected) => selected.length === 0 ? <em>All Types</em> : selected.join(', ')}
                >
                  {ADMISSION_TYPES.map(t => {
                    const isSelected = (advancedFilters.admission_types || []).includes(t);
                    return (
                      <MenuItem key={t} value={t}>
                        <Checkbox checked={isSelected} size="small" />
                        <ListItemText primary={t} />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>RTE</InputLabel>
                <Select value={advancedFilters.is_rte || ''} label="RTE" onChange={(e) => handleFilterChange('is_rte', e.target.value)}>
                  <MenuItem value=""><em>All</em></MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Dates with clean labels */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField 
                size="small" 
                fullWidth 
                type="date" 
                slotProps={{ inputLabel: { shrink: true } }}
                label="Admission Date (From)" 
                value={advancedFilters.admission_date_from || ''} 
                onChange={(e) => handleFilterChange('admission_date_from', e.target.value)} 
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField 
                size="small" 
                fullWidth 
                type="date" 
                slotProps={{ inputLabel: { shrink: true } }}
                label="Admission Date (To)" 
                value={advancedFilters.admission_date_to || ''} 
                onChange={(e) => handleFilterChange('admission_date_to', e.target.value)} 
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField 
                size="small" 
                fullWidth 
                type="date" 
                slotProps={{ inputLabel: { shrink: true } }}
                label="Date of Birth (From)" 
                value={advancedFilters.date_of_birth_from || ''} 
                onChange={(e) => handleFilterChange('date_of_birth_from', e.target.value)} 
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField 
                size="small" 
                fullWidth 
                type="date" 
                slotProps={{ inputLabel: { shrink: true } }}
                label="Date of Birth (To)" 
                value={advancedFilters.date_of_birth_to || ''} 
                onChange={(e) => handleFilterChange('date_of_birth_to', e.target.value)} 
              />
            </Grid>

            {/* Availability Boolean Flags */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Has Mobile</InputLabel>
                <Select value={advancedFilters.has_mobile || ''} label="Has Mobile" onChange={(e) => handleFilterChange('has_mobile', e.target.value)}>
                  <MenuItem value=""><em>All</em></MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Has Email</InputLabel>
                <Select value={advancedFilters.has_email || ''} label="Has Email" onChange={(e) => handleFilterChange('has_email', e.target.value)}>
                  <MenuItem value=""><em>All</em></MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Has Aadhaar</InputLabel>
                <Select value={advancedFilters.has_aadhaar || ''} label="Has Aadhaar" onChange={(e) => handleFilterChange('has_aadhaar', e.target.value)}>
                  <MenuItem value=""><em>All</em></MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField size="small" fullWidth label="Father Name" placeholder="Enter father name" value={advancedFilters.father_name || ''} onChange={(e) => handleFilterChange('father_name', e.target.value)} />
            </Grid>

            {/* Parent & Guardian Info */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField size="small" fullWidth label="Father Mobile" placeholder="Enter father mobile" value={advancedFilters.father_mobile || ''} onChange={(e) => handleFilterChange('father_mobile', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField size="small" fullWidth label="Father Email" placeholder="Enter father email" value={advancedFilters.father_email || ''} onChange={(e) => handleFilterChange('father_email', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField size="small" fullWidth label="Father Occupation" placeholder="e.g. Farmer" value={advancedFilters.father_occupation || ''} onChange={(e) => handleFilterChange('father_occupation', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField size="small" fullWidth label="Mother Name" placeholder="Enter mother name" value={advancedFilters.mother_name || ''} onChange={(e) => handleFilterChange('mother_name', e.target.value)} />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField size="small" fullWidth label="Mother Mobile" placeholder="Enter mother mobile" value={advancedFilters.mother_mobile || ''} onChange={(e) => handleFilterChange('mother_mobile', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField size="small" fullWidth label="Mother Email" placeholder="Enter mother email" value={advancedFilters.mother_email || ''} onChange={(e) => handleFilterChange('mother_email', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField size="small" fullWidth label="Mother Occupation" placeholder="e.g. Consultant" value={advancedFilters.mother_occupation || ''} onChange={(e) => handleFilterChange('mother_occupation', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField size="small" fullWidth label="Guardian Name" placeholder="Enter guardian name" value={advancedFilters.guardian_name || ''} onChange={(e) => handleFilterChange('guardian_name', e.target.value)} />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField size="small" fullWidth label="Guardian Relation" placeholder="e.g. FATHER / UNCLE" value={advancedFilters.guardian_relation || ''} onChange={(e) => handleFilterChange('guardian_relation', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField size="small" fullWidth label="Guardian Mobile" placeholder="Enter guardian mobile" value={advancedFilters.guardian_mobile || ''} onChange={(e) => handleFilterChange('guardian_mobile', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField size="small" fullWidth type="number" label="Annual Income Min (₹)" placeholder="Min income" value={advancedFilters.annual_income_min || ''} onChange={(e) => handleFilterChange('annual_income_min', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField size="small" fullWidth type="number" label="Annual Income Max (₹)" placeholder="Max income" value={advancedFilters.annual_income_max || ''} onChange={(e) => handleFilterChange('annual_income_max', e.target.value)} />
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button variant="outlined" color="inherit" onClick={onClear} startIcon={<X size={16} />}>
              Clear Filters
            </Button>
            <Button variant="contained" color="primary" onClick={onApply} startIcon={<Filter size={16} />}>
              Apply Filters
            </Button>
          </Box>
        </Box>
      </Collapse>

      {/* Active Filters Strip */}
      {activeChips.length > 0 && (
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ color: tokens.colors.text.muted, fontWeight: 600, mr: 1 }}>
            Active Filters:
          </Typography>
          {activeChips.map((chip) => (
            <Chip
              key={chip.key}
              label={chip.label}
              onDelete={() => removeChip(chip.key)}
              size="small"
              sx={{ 
                bgcolor: `${tokens.colors.primary.main}15`, 
                color: tokens.colors.primary.main,
                fontWeight: 500,
                borderRadius: 1
              }}
            />
          ))}
          {activeChips.length > 1 && (
            <Button size="small" color="error" sx={{ textTransform: 'none', ml: 1, p: 0.5, minWidth: 'auto' }} onClick={onClear}>
              Clear All
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}
